/* pi-header.js =========================================================
   Sticky-header behaviour (Dawn-style section sticky) + cart-bubble pulse.
   Announcement bar and header can stick independently via separate settings.
   ====================================================================== */
(function () {
  if (window.customElements.get('pi-sticky-header')) return;

  class PiStickyHeader extends HTMLElement {
    constructor() {
      super();
      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.headerBounds = {};
      this.updateStickyOffset = this.updateStickyOffset.bind(this);
    }

    connectedCallback() {
      this.section = this.closest('.pi-section-header');
      if (!this.section) return;

      this.type = this.getAttribute('data-sticky-type') || 'on-scroll-up';
      this.alwaysSticky = this.type === 'always' || this.type === 'reduce-logo-size';

      this.setHeaderHeight();
      this.updateStickyOffset();

      this.onResize = () => {
        this.setHeaderHeight();
        this.updateStickyOffset();
      };
      window.addEventListener('resize', this.onResize);
      window.matchMedia('(max-width: 990px)').addEventListener('change', this.onResize);
      document.addEventListener('pi:announcement-offset-update', this.updateStickyOffset);

      if (typeof ResizeObserver !== 'undefined') {
        this.offsetObserver = new ResizeObserver(this.updateStickyOffset);
        document
          .querySelectorAll('.pi-section-announcement.pi-section-announcement-sticky, pi-sticky-announcement[data-sticky="true"]')
          .forEach((el) => {
            this.offsetObserver.observe(el);
          });
      }

      if (this.type !== 'none') {
        if (this.alwaysSticky) {
          this.section.classList.add('pi-section-header-sticky');
        }

        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

        this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.addEventListener('scroll', this.onScrollHandler, { passive: true });
        this.createObserver();
      }

      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.pulseBubble());
        }
      } catch (e) {
        /* pubsub may not be ready in the editor; ignore. */
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      if (this.onScrollHandler) window.removeEventListener('scroll', this.onScrollHandler);
      if (this.offsetObserver) this.offsetObserver.disconnect();
      document.removeEventListener('pi:announcement-offset-update', this.updateStickyOffset);
      if (this.unsub) this.unsub();
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    }

    pulseBubble() {
      const bubble = this.querySelector('.pi-cart-bubble');
      if (!bubble) return;
      bubble.dataset.pulse = 'true';
      setTimeout(() => (bubble.dataset.pulse = 'false'), 600);
    }

    setHeaderHeight() {
      const height = this.offsetHeight || this.section.offsetHeight;
      document.documentElement.style.setProperty('--pi-header-height', `${height}px`);
    }

    updateStickyOffset() {
      if (typeof window.piUpdateAnnouncementOffset === 'function') {
        window.piUpdateAnnouncementOffset();
        return;
      }

      const stickySection = document.querySelector('.pi-section-announcement.pi-section-announcement-sticky');
      const fixedBar = document.querySelector('pi-sticky-announcement.pi-sticky-announcement--fixed');
      let offset = 0;

      if (stickySection) {
        offset = stickySection.offsetHeight;
      } else if (fixedBar) {
        offset = fixedBar.offsetHeight;
      }

      document.documentElement.style.setProperty('--pi-sticky-announcement-offset', `${offset}px`);
    }

    createObserver() {
      const observer = new IntersectionObserver((entries, obs) => {
        this.headerBounds = entries[0].intersectionRect;
        obs.disconnect();
      });
      observer.observe(this.section);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrolled = scrollTop > 40;
      this.dataset.scrolled = String(scrolled);

      if (this.type === 'none') return;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.section.classList.add('pi-section-header-scrolled-past');
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.section.classList.add('pi-section-header-scrolled-past');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);
          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);
          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.section.classList.remove('pi-section-header-scrolled-past');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.alwaysSticky || this.type !== 'on-scroll-up') return;
      this.section.classList.add('pi-section-header-hidden', 'pi-section-header-sticky');
    }

    reveal() {
      if (this.alwaysSticky || this.type !== 'on-scroll-up') return;
      this.section.classList.add('pi-section-header-sticky', 'pi-section-header-animate');
      this.section.classList.remove('pi-section-header-hidden');
    }

    reset() {
      if (this.alwaysSticky) return;
      this.section.classList.remove(
        'pi-section-header-hidden',
        'pi-section-header-sticky',
        'pi-section-header-animate'
      );
    }
  }

  window.customElements.define('pi-sticky-header', PiStickyHeader);
})();
